import React, { useEffect } from "react";
import { Row } from "antd";
import textStyles from "../components/Shared/Styles/TextStyles.module.scss";
import { useTripsCtx } from "../context/TripDetailsContext";
import { WandaTripStatus } from "../types/ApplicationTypes";
import { Tabs } from "../types/TripPaginationFilterTypes";
import { usePersonSummaryCtx } from "../context/PersonSummaryContext";
import { useDashboardCtx } from "../context/DashboardContext";

export const useDashboardTripServices = () => {
  const {
    getTripDetails,
    serviceDataCtx,
    totalServiceCountCtx,
    getFavoriteTripDetails,
    handleManageServiceUsersData,
  } = useTripsCtx();
  const { isLoadingPersonData } = usePersonSummaryCtx();

  const [serviceData] = serviceDataCtx;
  const [totalServiceCount] = totalServiceCountCtx;

  const {
    pageSizeCtx,
    sortOrderCtx,
    activeTabCtx,
    tripTypesCtx,
    rowsToSkipCtx,
    currentPageCtx,
    forceRefetchCtx,
    showCanceledCtx,
    pastDateRangeCtx,
    upcomingDateRangeCtx,
    favoriteDateRangeCtx,
    selectedPersonIdCtx,
  } = useDashboardCtx();

  const [activeTab] = activeTabCtx;
  const [sortOrder] = sortOrderCtx;
  const [tripTypes] = tripTypesCtx;
  const [showCanceled] = showCanceledCtx;
  const [forceRefetch] = forceRefetchCtx;
  const [pastDateRange] = pastDateRangeCtx;
  const [pageSize, setPageSize] = pageSizeCtx;
  const [selectedPersonId] = selectedPersonIdCtx;
  const [upcomingDateRange] = upcomingDateRangeCtx;
  const [favoriteDateRange] = favoriteDateRangeCtx;
  const [rowsToSkip, setRowsToSkip] = rowsToSkipCtx;
  const [currentPage, setCurrentPage] = currentPageCtx;

  const sortDateColumn = "Trip Date";

  useEffect(() => {
    !isLoadingPersonData &&
      !serviceData.length &&
      handleManageServiceUsersData();
  }, [isLoadingPersonData]);

  useEffect(() => {
    !isLoadingPersonData &&
      activeTab === Tabs.UPCOMING &&
      getTripDetails(
        WandaTripStatus.Booked,
        rowsToSkip[activeTab],
        pageSize[activeTab],
        upcomingDateRange.startDate,
        upcomingDateRange.endDate,
        selectedPersonId,
        sortDateColumn,
        sortOrder[activeTab]
      );
  }, [
    isLoadingPersonData,
    rowsToSkip[Tabs.UPCOMING],
    pageSize[Tabs.UPCOMING],
    activeTab,
    tripTypes[Tabs.UPCOMING],
    upcomingDateRange,
    selectedPersonId,
    sortOrder[Tabs.UPCOMING],
    forceRefetch[Tabs.UPCOMING],
  ]);

  useEffect(() => {
    !isLoadingPersonData &&
      activeTab === Tabs.PAST &&
      getTripDetails(
        showCanceled
          ? `${WandaTripStatus.Cancelled},${WandaTripStatus.Completed}`
          : WandaTripStatus.Completed,
        rowsToSkip[activeTab],
        pageSize[activeTab],
        pastDateRange.startDate,
        pastDateRange.endDate,
        selectedPersonId,
        sortDateColumn,
        sortOrder[activeTab]
      );
  }, [
    isLoadingPersonData,
    rowsToSkip[Tabs.PAST],
    pageSize[Tabs.PAST],
    activeTab,
    tripTypes[Tabs.PAST],
    pastDateRange,
    selectedPersonId,
    showCanceled,
    sortOrder[Tabs.PAST],
  ]);

  useEffect(() => {
    !isLoadingPersonData &&
      activeTab === Tabs.FAVORITE &&
      getFavoriteTripDetails(
        rowsToSkip[activeTab],
        pageSize[activeTab],
        favoriteDateRange.startDate,
        favoriteDateRange.endDate,
        selectedPersonId,
        sortOrder[activeTab]
      );
  }, [
    isLoadingPersonData,
    rowsToSkip[Tabs.FAVORITE],
    pageSize[Tabs.FAVORITE],
    activeTab,
    tripTypes[Tabs.FAVORITE],
    forceRefetch[Tabs.FAVORITE],
    selectedPersonId,
    sortOrder[Tabs.FAVORITE],
  ]);

  const handlePageChange = (page: number, pageSize?: number) => {
    const rowsToSkip = pageSize ? (page - 1) * pageSize : 0;
    setRowsToSkip((prev: { [tab: string]: number }) => ({
      ...prev,
      [activeTab]: rowsToSkip,
    }));
    setCurrentPage((prev: { [tab: string]: number }) => ({
      ...prev,
      [activeTab]: page,
    }));
  };

  const handlePageSizeChange = (current: number, size: number) =>
    setPageSize((prev: { [tab: string]: number }) => ({
      ...prev,
      [activeTab]: size,
    }));

  const paginationConfig = {
    current: currentPage[activeTab],
    pageSize: pageSize[activeTab],
    onChange: handlePageChange,
    total: totalServiceCount[activeTab],
    showSizeChanger: true,
    onShowSizeChange: handlePageSizeChange,
    showTotal: () => (
      <Row className={textStyles.normalText14}>
        Total {totalServiceCount[activeTab]} items
      </Row>
    ),
  };

  return { paginationConfig };
};
